import { MfaContext } from '@bumble/app/context';
import { apiError } from '@bumble/app/utils/api-error';
import queryClient from '@bumble/app/utils/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { Account } from '../__generated__/data-contracts';
import { SESSION_STORAGE_KEY } from '../constants';
import { useSubscriber } from '../context/SubscriberContext';
import { useApi } from './use-api';

export interface Session {
  access_token: string;
  username: string;
  session_type: string;
}

export function useAccount() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscriber, setSubscriber } = useSubscriber();
  const mfaContext = useContext(MfaContext);

  const [session, setSession] = useState<Session | null>(
    JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) ?? 'null'),
  );
  const [account, setAccount] = useState<Account | null>(null);

  const {
    data: accountData,
    isLoading,
    refetch: accountRefetch,
  } = useQuery(
    ['account', session],
    api.listAccountApiV1ListAccountGet.bind(null, { username: session?.username }),
    { enabled: !!session },
  );

  useEffect(() => {
    api.setSecurityData(session);
  }, [api, session]);

  useEffect(() => {
    setAccount((accountData?.data[0] ?? null) as Account);
  }, [accountData]);

  const authenticate = (sessionObj: Session, redirect: string) => {
    const { access_token, username, session_type } = sessionObj;
    const newSession = { username, access_token, session_type };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
    setSession(newSession);
    toast.success('You successfully logged in');
    navigate(redirect);
  };

  const from = ((location.state as any)?.from.pathname as string) || '/main-dashboard';
  const { mutate: login, isLoading: isLoggingIn } = useMutation(api.loginApiV1TokenPost, {
    // @ts-expect-error - api type is not correct
    onSuccess: ({ data: { access_token, session_type, detail } }, { username, password }) => {
      if (!access_token) {
        if (mfaContext) {
          toast.warning(detail);
          mfaContext.setUser({ email: username, password });
          navigate('/login-mfa');
        }
      } else {
        if (mfaContext) {
          mfaContext.setUser(null);
        }
        authenticate({ access_token, username, session_type }, from);
        // const newSession = { username, access_token, session_type };
        // localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
        // setSession(newSession);
        // toast.success('You successfully logged in');
        // navigate(from);
        if (subscriber) {
          setSubscriber(null);
        }
      }
    },
    onError: apiError,
  });

  return {
    account,
    accountRefetch,
    authenticate,
    isLoading,
    isLoggingIn,
    login,
    logout: () => {
      setSession(null);
      setAccount(null);
      localStorage.removeItem(SESSION_STORAGE_KEY);
      queryClient.clear();
      // api.setSecurityData(null);
    },
    session,
    storageToken: () => {
      return JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) ?? 'null');
    },
  };
}